/**
 * Google Analytics 4 Integration
 *
 * Provides GA4 initialization, page tracking, and custom event tracking.
 * Respects user consent preferences for GDPR compliance.
 *
 * @version 1.0.0
 */

import ReactGA from 'react-ga4';
import { env, isProduction } from './env';

// Analytics state
let isInitialized = false;
let hasConsent = false;

/**
 * Analytics event categories
 */
export type EventCategory =
  | 'engagement'
  | 'conversion'
  | 'navigation'
  | 'form'
  | 'content';

/**
 * Standard event names for consistency
 */
export type StandardEvent =
  | 'contact_form_submit'
  | 'newsletter_signup'
  | 'booking_initiated'
  | 'booking_completed'
  | 'resource_download'
  | 'blog_read'
  | 'service_view'
  | 'cta_click'
  | 'page_scroll'
  | 'outbound_link'
  | 'search'
  | 'share';

/**
 * Event parameters interface
 */
export interface EventParams {
  // Form events
  formType?: 'general' | 'urgent' | 'newsletter';
  formName?: string;

  // Content events
  source?: 'footer' | 'popup' | 'blog' | 'header' | 'sidebar' | 'cta';
  postSlug?: string;
  readTime?: number;
  scrollDepth?: number;

  // Service/resource events
  serviceType?: string;
  resourceId?: string;
  resourceTitle?: string;

  // CTA events
  ctaLocation?: string;
  ctaText?: string;

  // Link events
  linkUrl?: string;
  linkText?: string;

  // Search events
  searchTerm?: string;
  searchResults?: number;

  // Share events
  shareMethod?: 'twitter' | 'linkedin' | 'facebook' | 'email' | 'copy';
  shareContent?: string;

  // Generic params
  category?: EventCategory;
  label?: string;
  value?: number;
  nonInteraction?: boolean;

  // Custom params
  [key: string]: string | number | boolean | undefined;
}

/**
 * Initialize Google Analytics
 *
 * Should be called once when the app loads, typically in main.tsx or App.tsx.
 * Will only initialize if:
 * 1. A valid measurement ID is configured
 * 2. User has given consent (or consent is not required)
 *
 * @param forceInit - Force initialization regardless of consent (for testing)
 */
export function initializeAnalytics(forceInit = false): void {
  if (isInitialized) {
    return;
  }

  const measurementId = env().VITE_GA_MEASUREMENT_ID;

  if (!measurementId) {
    if (!isProduction()) {
      console.info('[Analytics] No measurement ID configured, skipping initialization');
    }
    return;
  }

  // Check consent unless forced
  if (!forceInit && !hasConsent) {
    // Check if consent was previously given (stored in localStorage)
    const storedConsent = localStorage.getItem('analytics_consent');
    if (storedConsent !== 'granted') {
      if (!isProduction()) {
        console.info('[Analytics] Waiting for user consent');
      }
      return;
    }
    hasConsent = true;
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        anonymizeIp: true,
        siteSpeedSampleRate: 100,
      },
      gtagOptions: {
        send_page_view: false, // We'll send manually for better control
      },
    });

    isInitialized = true;

    if (!isProduction()) {
      console.info('[Analytics] Initialized with ID:', measurementId);
    }
  } catch (error) {
    console.error('[Analytics] Initialization failed:', error);
  }
}

/**
 * Set user consent for analytics
 *
 * Call this when the user accepts or rejects analytics cookies.
 * If accepted, analytics will be initialized.
 *
 * @param consent - Whether the user has given consent
 */
export function setAnalyticsConsent(consent: boolean): void {
  hasConsent = consent;
  localStorage.setItem('analytics_consent', consent ? 'granted' : 'denied');

  if (consent && !isInitialized) {
    initializeAnalytics(true);
  } else if (!consent && isInitialized) {
    // Disable tracking (GA4 doesn't have a clean disable, but we can prevent future events)
    isInitialized = false;
  }

  // Update GA consent mode if initialized
  if (isInitialized && typeof window !== 'undefined' && 'gtag' in window) {
    (window as typeof window & { gtag: (...args: unknown[]) => void }).gtag('consent', 'update', {
      analytics_storage: consent ? 'granted' : 'denied',
    });
  }
}

/**
 * Check if user has given analytics consent
 */
export function hasAnalyticsConsent(): boolean {
  return localStorage.getItem('analytics_consent') === 'granted';
}

/**
 * Track a page view
 *
 * Automatically called on route changes when using the useAnalytics hook.
 *
 * @param path - The page path (defaults to current location)
 * @param title - The page title (defaults to document title)
 */
export function trackPageView(path?: string, title?: string): void {
  if (!isInitialized) {
    return;
  }

  const pagePath = path || window.location.pathname + window.location.search;
  const pageTitle = title || document.title;

  ReactGA.send({
    hitType: 'pageview',
    page: pagePath,
    title: pageTitle,
  });

  if (!isProduction()) {
    console.info('[Analytics] Page view:', { path: pagePath, title: pageTitle });
  }
}

/**
 * Track a custom event
 *
 * @param eventName - The event name (use StandardEvent for consistency)
 * @param params - Event parameters
 */
export function trackEvent(eventName: StandardEvent | string, params?: EventParams): void {
  if (!isInitialized) {
    return;
  }

  // Clean up params - remove undefined values
  const cleanParams: Record<string, string | number | boolean> = {};
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanParams[key] = value;
      }
    });
  }

  ReactGA.event(eventName, cleanParams);

  if (!isProduction()) {
    console.info('[Analytics] Event:', eventName, cleanParams);
  }
}

/**
 * Track contact form submission
 */
export function trackContactFormSubmit(formType: 'general' | 'urgent' = 'general'): void {
  trackEvent('contact_form_submit', {
    category: 'conversion',
    formType,
  });
}

/**
 * Track newsletter signup
 */
export function trackNewsletterSignup(source: EventParams['source'] = 'footer'): void {
  trackEvent('newsletter_signup', {
    category: 'conversion',
    source,
  });
}

/**
 * Track booking initiation
 */
export function trackBookingInitiated(serviceType?: string): void {
  const params: EventParams = {
    category: 'conversion',
  };
  if (serviceType) {
    params.serviceType = serviceType;
  }
  trackEvent('booking_initiated', params);
}

/**
 * Track booking completion
 */
export function trackBookingCompleted(serviceType?: string): void {
  const params: EventParams = {
    category: 'conversion',
    value: 1,
  };
  if (serviceType) {
    params.serviceType = serviceType;
  }
  trackEvent('booking_completed', params);
}

/**
 * Track resource download
 */
export function trackResourceDownload(resourceId: string, resourceTitle: string): void {
  trackEvent('resource_download', {
    category: 'engagement',
    resourceId,
    resourceTitle,
  });
}

/**
 * Track blog post read
 */
export function trackBlogRead(postSlug: string, readTime?: number): void {
  const params: EventParams = {
    category: 'engagement',
    postSlug,
  };
  if (readTime !== undefined) {
    params.readTime = readTime;
  }
  trackEvent('blog_read', params);
}

/**
 * Track service page view
 */
export function trackServiceView(serviceType: string): void {
  trackEvent('service_view', {
    category: 'engagement',
    serviceType,
  });
}

/**
 * Track CTA click
 */
export function trackCtaClick(ctaLocation: string, ctaText: string): void {
  trackEvent('cta_click', {
    category: 'engagement',
    ctaLocation,
    ctaText,
  });
}

/**
 * Track outbound link click
 */
export function trackOutboundLink(linkUrl: string, linkText?: string): void {
  const params: EventParams = {
    category: 'navigation',
    linkUrl,
  };
  if (linkText) {
    params.linkText = linkText;
  }
  trackEvent('outbound_link', params);
}

/**
 * Track scroll depth
 */
export function trackScrollDepth(depth: number): void {
  trackEvent('page_scroll', {
    category: 'engagement',
    scrollDepth: depth,
    nonInteraction: true,
  });
}

/**
 * Track social share
 */
export function trackShare(method: EventParams['shareMethod'], content: string): void {
  const params: EventParams = {
    category: 'engagement',
    shareContent: content,
  };
  if (method) {
    params.shareMethod = method;
  }
  trackEvent('share', params);
}

/**
 * Set user properties (for logged-in users, if applicable)
 */
export function setUserProperties(properties: Record<string, string | number | boolean>): void {
  if (!isInitialized) {
    return;
  }

  ReactGA.set(properties);

  if (!isProduction()) {
    console.info('[Analytics] User properties set:', properties);
  }
}

/**
 * Check if analytics is initialized
 */
export function isAnalyticsInitialized(): boolean {
  return isInitialized;
}

// Export default functions for convenience
export default {
  initialize: initializeAnalytics,
  setConsent: setAnalyticsConsent,
  hasConsent: hasAnalyticsConsent,
  trackPageView,
  trackEvent,
  trackContactFormSubmit,
  trackNewsletterSignup,
  trackBookingInitiated,
  trackBookingCompleted,
  trackResourceDownload,
  trackBlogRead,
  trackServiceView,
  trackCtaClick,
  trackOutboundLink,
  trackScrollDepth,
  trackShare,
  setUserProperties,
  isInitialized: isAnalyticsInitialized,
};
